import { summarizeIncentivePackage } from '@develio/shared';

interface SummaryNumbers {
  readonly totalIncentive: number;
  readonly perJobCost: number | null;
  readonly rateOfReturn: number | null;
  readonly paybackYears: number | null;
}

interface Props {
  readonly inputs: unknown;
  readonly summary: SummaryNumbers;
}

/**
 * Top-of-page incentives summary panel.
 *
 * Shows the EDC professional, at a glance:
 *  1. The size of the offered package and its key efficiency ratios.
 *  2. Which levers are being used and on what terms.
 *
 * The lever list comes from `summarizeIncentivePackage` (shared) so the
 * PDF report renders the same labels and identical lever-inclusion rules.
 *
 * Hidden when no levers are offered — an analysis with `step8` all zeros
 * shouldn't waste vertical space announcing "no incentives".
 */
export function IncentivesSummaryBox({ inputs, summary }: Props) {
  const pkg = summarizeIncentivePackage(inputs as Parameters<typeof summarizeIncentivePackage>[0]);

  if (pkg.isEmpty) return null;

  return (
    <section
      aria-label="Incentive package summary"
      className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 shadow-sm"
    >
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="font-display text-lg font-semibold text-slate-900">Incentive package</h3>
        <span className="text-xs font-medium uppercase tracking-wider text-indigo-700">
          As offered
        </span>
      </div>

      {/* Headline numbers — total, per-job, ROI, payback */}
      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Stat label="Total cost" value={fmtCurrency(summary.totalIncentive)} emphasis />
        <Stat
          label="Per job"
          value={summary.perJobCost !== null ? fmtCurrency(summary.perJobCost) : 'N/A'}
        />
        <Stat
          label="Rate of return"
          value={
            summary.rateOfReturn !== null ? `${(summary.rateOfReturn * 100).toFixed(1)}%` : 'N/A'
          }
        />
        <Stat
          label="Payback"
          value={
            summary.paybackYears !== null
              ? `${summary.paybackYears.toFixed(1)} yrs`
              : 'Not within term'
          }
        />
      </div>

      {/* Active levers — only those with non-zero terms */}
      <ul className="mt-4 divide-y divide-indigo-100 border-t border-indigo-100">
        {pkg.levers.map((lever) => (
          <li
            key={lever.id}
            className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm"
          >
            <span className="font-medium text-slate-900">{lever.label}</span>
            <span className="text-slate-700">{lever.terms}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Stat({
  label,
  value,
  emphasis = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly emphasis?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className={
          emphasis
            ? 'mt-0.5 font-display text-2xl font-semibold text-slate-900'
            : 'mt-0.5 font-display text-base font-semibold text-slate-900'
        }
      >
        {value}
      </div>
    </div>
  );
}

function fmtCurrency(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}
